import { Button, Stack, TypographyProps } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import EditIcon from "@/common/components/Icons/Svg/EditIcon";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
  WashDto,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import WashMenu from "../WashMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum WashViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  Vehicle = "Vehicle",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};
const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export interface WashViewProps extends BaseEntityViewInheritableProps<WashDto> {
  washId: string | null | undefined;
  wash: WashDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function WashView({
  washId,
  wash,
  displayProps,
  headerProps,
  onUpdate,
  onDelete,
  onClose,
}: WashViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.Wash),
      viewVariant: displayProps?.viewVariant,
    },
  });

  const washRequest = useApiRequest(
    apiClient.washesApi.apiV1WashesWashIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      washId: washId!,
    },
    {
      deps: [washId],
      skip: !washId,
    },
  );
  wash = wash || washRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [DataUpdatesChannelName.Entity(currentTenant?.id, EntityType.Wash, washId || "")],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      washRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: wash && {
      idValue: wash?.id || "",
      newTitle: wash?.localNumber || "",
    },
  });

  return (
    <BaseEntityView
      entityType={EntityType.Wash}
      entityId={washId}
      entity={wash}
      entityRequest={washRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          wash && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.Wash}
              operationEntity={wash}
              vehicleId={wash?.vehicle?.id}
              vehicle={wash?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink ? ROUTE_PATH.WASH_VIEW(wash?.id) : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps?.actions &&
                wash && (
                  <Stack direction='row' spacing={1}>
                    <AuthorizedElement permissions={[AppPermission.WashManage]}>
                      <Button
                        variant='outlined'
                        color='text'
                        component={RouterLink}
                        to={ROUTE_PATH.WASH_EDIT(wash?.id)}
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                    </AuthorizedElement>
                  </Stack>
                )
              }
              secondaryActions={
                wash && (
                  <WashMenu
                    entity={wash}
                    displayProps={{
                      actions: {
                        edit: false,
                        delete: true,
                        addToAssignment: true,
                      },
                    }}
                    onUpdate={(newValue) => {
                      newValue ? washRequest.replaceData(newValue) : washRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                washRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={WashViewPageTabs}
          defaultTabId={WashViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: WashViewPageTabs.Overview, isHideable: false },
            { label: "Chats", value: WashViewPageTabs.Inbox },
            {
              label: "Vehicle",
              value: WashViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            {
              label: "History",
              value: WashViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: WashViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            wash && (
              <>
                {activeTab === WashViewPageTabs.Overview && (
                  <OverviewTabContent
                    onUpdate={(newValue) => {
                      newValue ? washRequest.replaceData(newValue) : washRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    wash={wash}
                  />
                )}
                {activeTab === WashViewPageTabs.Inbox && <InboxTabContent wash={wash} />}
                {activeTab === WashViewPageTabs.Vehicle && <VehicleTabContent wash={wash} />}
                {activeTab === WashViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent wash={wash} />
                )}
                {activeTab === WashViewPageTabs.Comments && <CommentsTabContent wash={wash} />}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
